import { useEffect, useState } from 'preact/hooks'
import { Plus, KeyRound } from 'lucide-preact'
import { inboundWebhooksApi } from '@/api/inboundWebhooks'
import { useInboundWebhookSubscriptions } from '@/hooks/useInboundWebhooks'
import { INBOUND_WEBHOOK_STATUS_COLORS, type InboundWebhookSubscriptionViewDto } from '@/types/inboundWebhooks'
import { OneTimeApiKeyModal } from '../components/OneTimeApiKeyModal'
import { InboundWebhookSnippet } from '../components/InboundWebhookSnippet'

interface InboundWebhooksPanelProps {
    clientCompanyId: string
    workspaces: { Id: string; Name: string }[]
}

type Mode = { kind: 'view' } | { kind: 'create' }

const inputClass =
    'w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500'

/**
 * Inbound webhook keys: master-detail, matching the outbound panel beside it.
 *
 * Each key names exactly one workspace, so the rail reads as a list of destinations and the detail
 * can render a snippet that is already correct for that workspace — only the web's id is
 * left for the integrator to fill in, and the web settings tab fills that in too.
 */
export function InboundWebhooksPanel({ clientCompanyId, workspaces }: InboundWebhooksPanelProps) {
    const { subscriptions, loading, error, reload } = useInboundWebhookSubscriptions(clientCompanyId)

    const [selectedId, setSelectedId] = useState<string | null>(null)
    const [mode, setMode] = useState<Mode>({ kind: 'view' })
    const [saving, setSaving] = useState(false)
    const [formError, setFormError] = useState<string | null>(null)
    const [revealedKey, setRevealedKey] = useState<string | null>(null)

    const [name, setName] = useState('')
    const [workspaceId, setWorkspaceId] = useState('')
    const [description, setDescription] = useState('')

    useEffect(() => {
        if (subscriptions && subscriptions.length > 0 && !selectedId) {
            setSelectedId(subscriptions[0].Id)
        }
    }, [subscriptions, selectedId])

    const selected = subscriptions?.find(subscription => subscription.Id === selectedId) ?? null
    const workspaceName = (id: string) => workspaces.find(workspace => workspace.Id === id)?.Name ?? id

    const startCreate = () => {
        setName('')
        // Preselect rather than leaving blank: the field is required, and with one workspace (the
        // common case today) there is nothing to choose.
        setWorkspaceId(workspaces[0]?.Id ?? '')
        setDescription('')
        setFormError(null)
        setMode({ kind: 'create' })
    }

    const submitCreate = async () => {
        setSaving(true)
        setFormError(null)
        const response = await inboundWebhooksApi.createSubscription(clientCompanyId, {
            Name: name.trim(),
            WorkspaceId: workspaceId,
            Description: description.trim() || undefined,
        })
        setSaving(false)

        if (response.error || !response.data) {
            setFormError(response.error ?? 'Could not create the key.')
            return
        }

        setRevealedKey(response.data.Key)
        setSelectedId(response.data.Subscription.Id)
        setMode({ kind: 'view' })
        await reload()
    }

    const rotate = async (subscription: InboundWebhookSubscriptionViewDto) => {
        const response = await inboundWebhooksApi.rotateKey(clientCompanyId, subscription.Id)
        if (response.error || !response.data) {
            setFormError(response.error ?? 'Could not rotate the key.')
            return
        }
        setRevealedKey(response.data.Key)
        await reload()
    }

    const togglePaused = async (subscription: InboundWebhookSubscriptionViewDto) => {
        const response = await inboundWebhooksApi.updateSubscription(clientCompanyId, subscription.Id, {
            Status: subscription.Status === 'Active' ? 'Paused' : 'Active',
        })
        if (response.error) {
            setFormError(response.error)
            return
        }
        await reload()
    }

    const remove = async (subscription: InboundWebhookSubscriptionViewDto) => {
        const response = await inboundWebhooksApi.deleteSubscription(clientCompanyId, subscription.Id)
        if (response.error) {
            setFormError(response.error)
            return
        }
        setSelectedId(null)
        await reload()
    }

    if (loading && !subscriptions) {
        return <p className="text-sm text-slate-500 dark:text-slate-400">Loading inbound webhooks…</p>
    }

    return (
        <div className="space-y-3">
            {error && (
                <div className="p-2 rounded border border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-300 text-xs">
                    {error}
                </div>
            )}
            {formError && (
                <div className="p-2 rounded border border-red-200 bg-red-50 text-red-800 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300 text-xs">
                    {formError}
                </div>
            )}

            {mode.kind === 'create' ? (
                <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4 space-y-3">
                    <h4 className="text-sm font-semibold text-slate-900 dark:text-white">New inbound webhook key</h4>

                    <label className="block text-sm">
                        <span className="text-slate-700 dark:text-slate-300">Name</span>
                        <input
                            className={`${inputClass} mt-1`}
                            value={name}
                            onInput={event => setName((event.currentTarget as HTMLInputElement).value)}
                            placeholder="Partner AI SDR"
                            maxLength={100}
                        />
                    </label>

                    <label className="block text-sm">
                        <span className="text-slate-700 dark:text-slate-300">Workspace</span>
                        <span className="block text-xs text-slate-500 dark:text-slate-400 mb-1">
                            Leads posted with this key land in this workspace. A key cannot be moved later &mdash; that would silently
                            redirect live traffic &mdash; so issue a second key for a second workspace.
                        </span>
                        <select
                            className={inputClass}
                            value={workspaceId}
                            onChange={event => setWorkspaceId((event.currentTarget as HTMLSelectElement).value)}
                        >
                            {workspaces.map(workspace => (
                                <option key={workspace.Id} value={workspace.Id}>{workspace.Name}</option>
                            ))}
                        </select>
                    </label>

                    <label className="block text-sm">
                        <span className="text-slate-700 dark:text-slate-300">Notes</span>
                        <input
                            className={`${inputClass} mt-1`}
                            value={description}
                            onInput={event => setDescription((event.currentTarget as HTMLInputElement).value)}
                            placeholder="Who uses this key, and what for"
                            maxLength={500}
                        />
                    </label>

                    <p className="text-xs text-slate-500 dark:text-slate-400">
                        The key is shown once, immediately after it is created. Store it somewhere safe before closing that dialog.
                    </p>

                    <div className="flex gap-2">
                        <button
                            type="button"
                            disabled={saving || !name.trim() || !workspaceId}
                            onClick={() => void submitCreate()}
                            className="px-3 py-1.5 rounded-lg bg-primary-600 text-white text-sm disabled:opacity-50"
                        >
                            {saving ? 'Creating…' : 'Create key'}
                        </button>
                        <button
                            type="button"
                            onClick={() => setMode({ kind: 'view' })}
                            className="px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 text-sm text-slate-700 dark:text-slate-300"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-[272px_1fr] gap-4">
                    <div className="space-y-2">
                        <button
                            type="button"
                            onClick={startCreate}
                            disabled={workspaces.length === 0}
                            className="w-full flex items-center justify-center gap-1 px-3 py-2 rounded-lg border border-dashed border-slate-300 dark:border-slate-600 text-sm text-slate-600 dark:text-slate-300 disabled:opacity-50"
                        >
                            <Plus className="w-4 h-4" /> New key
                        </button>

                        {(subscriptions ?? []).map(subscription => (
                            <button
                                key={subscription.Id}
                                type="button"
                                onClick={() => setSelectedId(subscription.Id)}
                                className={`w-full text-left px-3 py-2 rounded-lg border text-sm ${
                                    subscription.Id === selectedId
                                        ? 'border-primary-400 bg-primary-50 dark:bg-primary-900/20'
                                        : 'border-slate-200 dark:border-slate-700'
                                }`}
                            >
                                <div className="font-medium text-slate-900 dark:text-white truncate">{subscription.Name}</div>
                                <div className="text-xs text-slate-500 dark:text-slate-400 truncate">
                                    {workspaceName(subscription.WorkspaceId)}
                                </div>
                            </button>
                        ))}

                        {(subscriptions ?? []).length === 0 && (
                            <p className="text-xs text-slate-500 dark:text-slate-400 px-1">
                                No keys yet. Create one to let an external system post leads in.
                            </p>
                        )}
                    </div>

                    <div className="min-w-0">
                        {selected ? (
                            <div className="space-y-4">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <h4 className="text-sm font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                                            <KeyRound className="w-4 h-4 text-slate-400" />
                                            {selected.Name}
                                        </h4>
                                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                            Writes into <span className="font-medium">{workspaceName(selected.WorkspaceId)}</span>
                                            {' · '}
                                            {selected.LastReceivedUtc
                                                ? `last used ${new Date(selected.LastReceivedUtc).toLocaleString()}`
                                                : 'never used'}
                                        </p>
                                        {selected.Description && (
                                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{selected.Description}</p>
                                        )}
                                    </div>
                                    <span className={`px-2 py-0.5 rounded text-xs whitespace-nowrap ${INBOUND_WEBHOOK_STATUS_COLORS[selected.Status] ?? ''}`}>
                                        {selected.Status}
                                    </span>
                                </div>

                                <div className="flex flex-wrap gap-2">
                                    <button
                                        type="button"
                                        onClick={() => void rotate(selected)}
                                        className="px-2.5 py-1 rounded-lg border border-slate-300 dark:border-slate-600 text-xs text-slate-700 dark:text-slate-300"
                                    >
                                        Rotate key
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => void togglePaused(selected)}
                                        className="px-2.5 py-1 rounded-lg border border-slate-300 dark:border-slate-600 text-xs text-slate-700 dark:text-slate-300"
                                    >
                                        {selected.Status === 'Active' ? 'Pause' : 'Resume'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => void remove(selected)}
                                        className="px-2.5 py-1 rounded-lg border border-red-300 dark:border-red-800 text-xs text-red-700 dark:text-red-400"
                                    >
                                        Delete
                                    </button>
                                </div>

                                <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3">
                                    <InboundWebhookSnippet />
                                </div>
                            </div>
                        ) : (
                            <p className="text-sm text-slate-500 dark:text-slate-400">Select a key to see how to use it.</p>
                        )}
                    </div>
                </div>
            )}

            <OneTimeApiKeyModal
                isOpen={revealedKey !== null}
                apiKey={revealedKey}
                title="Save your inbound webhook key"
                onAcknowledge={() => setRevealedKey(null)}
            />
        </div>
    )
}
